import React, { useState } from 'react';
import { TemplateElement, TemplateElementProps, ELEMENT_TYPES, ELEMENT_CATEGORIES } from './types';

const TemplateElementComponent: React.FC<TemplateElementProps> = ({
  element,
  isEditing,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onUpdate({ content: e.target.value });
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate({ type: e.target.value as TemplateElement['type'] });
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate({ category: e.target.value as TemplateElement['category'] });
  };

  const handleRequiredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate({ required: e.target.checked });
  };

  const handlePlaceholderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate({ placeholder: e.target.value });
  };

  const getTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      question: '❓',
      statement: '💬',
      checklist: '✅'
    };
    return icons[type] || '📝';
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      question: 'bg-blue-100 text-blue-800 border-blue-200',
      statement: 'bg-green-100 text-green-800 border-green-200',
      checklist: 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colors[type] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      opening: 'bg-yellow-100 text-yellow-800',
      discovery: 'bg-blue-100 text-blue-800',
      presentation: 'bg-purple-100 text-purple-800',
      closing: 'bg-green-100 text-green-800',
      'follow-up': 'bg-orange-100 text-orange-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className={`border-2 rounded-lg p-4 transition-all ${getTypeColor(element.type)}`}>
      {/* Header Row with all controls */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-3">
          <span className="text-lg">{getTypeIcon(element.type)}</span>
          <span className="text-sm font-medium">#{element.order}</span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(element.category)}`}>
            {element.category}
          </span>
          
          {/* Element Type Dropdown */}
          <select
            value={element.type}
            onChange={handleTypeChange}
            className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent bg-white"
          >
            {ELEMENT_TYPES.map(type => (
              <option key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>

          {/* Category Dropdown */}
          <select
            value={element.category}
            onChange={handleCategoryChange}
            className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent bg-white"
          >
            {ELEMENT_CATEGORIES.map(category => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>

          {/* Required Checkbox */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id={`required-${element.id}`}
              checked={element.required || false}
              onChange={handleRequiredChange}
              className="mr-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor={`required-${element.id}`} className="text-xs font-medium text-gray-700">
              Required
            </label>
          </div>
        </div>

        <div className="flex items-center space-x-1">
          {/* Move buttons */}
          <button
            onClick={onMoveUp}
            disabled={!canMoveUp}
            className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move Up"
          >
            ⬆️
          </button>
          <button
            onClick={onMoveDown}
            disabled={!canMoveDown}
            className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move Down"
          >
            ⬇️
          </button>

          {/* Delete button */}
          <button
            onClick={onDelete}
            className="p-1 text-red-500 hover:text-red-700"
            title="Delete Element"
          >
            🗑️
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-3">
        <textarea
          value={element.content}
          onChange={handleContentChange}
          placeholder={element.placeholder || 'Enter content here...'}
          rows={element.content.length > 100 ? 4 : 2}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-white"
        />

        {/* Placeholder Text Field */}
        <div>
          <input
            type="text"
            value={element.placeholder || ''}
            onChange={handlePlaceholderChange}
            placeholder="Placeholder text for this element..."
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
          />
        </div>
      </div>
    </div>
  );
};

export default TemplateElementComponent; 